import { HERD_GOAL_NAMES } from "../constants/chartConstants";

/**
 * Converts "Peer Average" to "National Average" in headers and names
 */
export const convertPeerToNationalAverage = (data, shouldConvert) => {
  if (!shouldConvert || !data) return data;

  return data.map((item) => ({
    ...item,
    header: item.header?.replace("Peer Average", "National Average"),
    name: item.name?.replace("Peer Average", "National Average"),
  }));
};

/**
 * Filters out herd goal series and returns the filtered data
 */
export const filterHerdGoals = (series) => {
  if (!series) return { filtered: null, removedGoal: "" };

  let removedGoal = "";
  const filtered = series.filter((item) => {
    if (HERD_GOAL_NAMES.includes(item.name)) {
      removedGoal = item.name;
      return false;
    }
    return true;
  });

  return { filtered, removedGoal };
};

/**
 * Converts zero values to null in data arrays
 */
export const convertZerosToNull = (series) => {
  if (!series) return series;

  return series.map((item) => ({
    ...item,
    data: item.data?.map((value) => (value === 0 ? null : value)),
  }));
};

/**
 * Extracts all data values from nested data structures
 */
export const extractAllDataValues = (dataArray) => {
  const values = [];
  dataArray.forEach((element) => {
    if (element.data) {
      values.push(...element.data);
    }
  });
  return values.filter((n) => n != null);
};

/**
 * Calculates min and max values from data array
 */
export const calculateMinMax = (dataValues) => {
  if (!dataValues || dataValues.length === 0) {
    return { min: 0, max: 0 };
  }

  const min = Math.min(...dataValues);
  const max = Math.max(...dataValues);

  return { min, max };
};

/**
 * Rounds value to nearest multiple
 */
export const roundToNearest = (value, multiple) => {
  return Math.round(value / multiple) * multiple;
};

/**
 * Removes duplicate series (e.g., Quarter Average if same as Test Day)
 */
export const removeDuplicateSeries = (series) => {
  if (!series || series.length === 0) return series;

  let testDayData = [];
  let shouldRemoveQuarterAvg = false;

  const filtered = [...series];

  series.forEach((item, idx) => {
    if (item.name === "test_day_1") {
      testDayData = item.data;
    }
    if (item.name === "Quarter_Average") {
      const areEqual =
        JSON.stringify(testDayData) === JSON.stringify(item.data);
      if (areEqual) {
        shouldRemoveQuarterAvg = true;
      }
    }
  });

  if (shouldRemoveQuarterAvg) {
    return filtered.filter((item) => item.name !== "Quarter_Average");
  }

  return filtered;
};

/**
 * Filters data with property check
 */
export const filterWithDataProperty = (dataArray) => {
  if (!dataArray) return [];
  return dataArray.filter((item) => item.hasOwnProperty("data"));
};

/**
 * Processes days open data for specific report tabs
 */
export const processDaysOpenData = (daysOpenSeries, categories, reportTab) => {
  if (!daysOpenSeries || reportTab === "snapsix") {
    return {
      series: daysOpenSeries ? [daysOpenSeries[0]] : [],
      categories: categories,
    };
  }

  let processed = [];

  // Process each series - take only data[0] which corresponds to categories[0]
  for (let i = 0; i < daysOpenSeries.length; i++) {
    const item = daysOpenSeries[i];

    if (i < 2) {
      // First two items (time periods) - always include with only first value
      processed.push({
        ...item,
        data: [item.data[0]],
      });
    } else if (
      reportTab === "periodcomparison" ||
      reportTab === "subherdcomparison"
    ) {
      // For period/subherd comparison, items from index 2+ (goals/targets)
      // Only include if data[0] has valid non-zero value
      if (
        item.data &&
        item.data.length > 0 &&
        item.data[0] !== null &&
        item.data[0] !== undefined &&
        item.data[0] !== "" &&
        item.data[0] !== " " &&
        item.data[0] !== 0
      ) {
        processed.push({
          ...item,
          data: [item.data[0]],
        });
      }
    } else {
      // For other report tabs, include items from index 2+ as-is
      processed.push(item);
    }
  }

  return {
    series: processed,
    categories: [categories[0]],
  };
};

/**
 * Checks if peer to national average conversion should occur
 */
export const shouldConvertPeerToNational = (reportTab, peerFooterDef) => {
  return (
    reportTab === "benchmark" &&
    typeof peerFooterDef?.Peer_Average_Availability !== "undefined"
  );
};
